import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { CtaComponent as OfferCta } from 'components/Offer/Compact/CtaComponent';
import { OfferInteraction } from 'components/Offer/Shared/OfferInteraction';
import { OfferPrice } from 'components/Offer/Shared/OfferPrice';
import { SplitBookingBundleType, SplitBookingOfferInfo } from 'components/Offer/types';
import { OfferTrackingContextProvider } from 'context/offerTracking';
import { getHotelDetailsVisibility } from 'modules/overlay/selectors';
import { getOfferEntity, getSearchParameters } from 'modules/sapiSearch/selectors';
import { getSearchBoxValues } from 'modules/searchBox/selectors';
import { getNumberOfNights } from 'utils/offers';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Text } from '@daedalus/atlas/Text';
import { SearchOffer } from '@daedalus/core/src/offer/types/SearchOffer';
import { ProviderLogo } from '@daedalus/shared/src/search/ProviderLogo';
import { useFormattedDate } from '../helpers';
import { messages } from '../messages';
const OfferDetails = /*#__PURE__*/styled('div')({
  name: "OfferDetails",
  class: "ozi3v5",
  propsAsIs: false
});
const Circle = /*#__PURE__*/styled('div')({
  name: "Circle",
  class: "cjwdx43",
  propsAsIs: false
});
const Separator = /*#__PURE__*/styled('div')({
  name: "Separator",
  class: "s1bfan28",
  propsAsIs: false
});
const Offer = /*#__PURE__*/styled('div')({
  name: "Offer",
  class: "o1ymm6ux",
  propsAsIs: false
});
const _exp14 = /*#__PURE__*/() => Text;
const RoomName = /*#__PURE__*/styled(_exp14())({
  name: "RoomName",
  class: "rh4fyh3",
  propsAsIs: true
});
const PriceAndCtaWrapper = /*#__PURE__*/styled('div')({
  name: "PriceAndCtaWrapper",
  class: "pxlhrxb",
  propsAsIs: false
});
const OfferCtaWrapper = /*#__PURE__*/styled('div')({
  name: "OfferCtaWrapper",
  class: "o1vunbfx",
  propsAsIs: false
});
const _exp16 = /*#__PURE__*/() => Flex.Column;
const PriceAndNightMessage = /*#__PURE__*/styled(_exp16())({
  name: "PriceAndNightMessage",
  class: "p1l08h48",
  propsAsIs: true
});
const _exp20 = /*#__PURE__*/() => Flex.Column;
const LogoAndRoomWrapper = /*#__PURE__*/styled(_exp20())({
  name: "LogoAndRoomWrapper",
  class: "lidox13",
  propsAsIs: true
});
interface Props {
  splitBookingOffer: SplitBookingOfferInfo;
  offerIdx: number;
  isAccommodationPage: boolean;
  isSeparatorVisible: boolean;
  splitBookingType: SplitBookingBundleType;
  splitBookingPosition: number;
}
export const SplitBookingDetailsOfferItem: React.FC<Props> = ({
  splitBookingOffer,
  isAccommodationPage = false,
  isSeparatorVisible = false,
  splitBookingPosition = 0,
  splitBookingType,
  offerIdx
}) => {
  const {
    formatDate
  } = useFormattedDate();
  const {
    formatMessage
  } = useIntl();
  const {
    isMobile,
    isDesktop
  } = useDeviceLayout();
  const {
    hotelId
  } = useSelector(getSearchBoxValues);
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId));
  const searchParameters = useSelector(getSearchParameters);
  const {
    offer,
    checkIn,
    checkOut
  } = splitBookingOffer;
  const offerNights = getNumberOfNights(checkIn, checkOut);
  const isHotelDetailsOverlayOpen = useSelector(getHotelDetailsVisibility);

  // Capitalizing the ::first-letter in CSS does not work in this case as we are using line-clamp
  const roomName = [offer.roomName?.charAt(0).toUpperCase(), offer.roomName?.toLocaleLowerCase().slice(1)].join('');
  const getEventComponentName = () => {
    if (isAccommodationPage) return 'MobileHotelViewSplitBookingOffersModal';
    if (isDesktop && isHotelDetailsOverlayOpen) {
      return 'DesktopAllDealsViewSplitBookingOffersModal';
    }
    return isMobile ? 'MobileSearchViewSplitBookingOffersModal' : 'DesktopSearchViewSplitBookingOffersModal';
  };
  return <Flex.Row>
      <OfferDetails>
        <Flex.Row alignItems="center">
          <Circle />
          <Text variant="labelM">
            {formatDate(checkIn)} - {formatDate(checkOut)} ({offerNights}{' '}
            {formatMessage(messages.nights, {
            count: offerNights
          })}
            )
          </Text>
        </Flex.Row>
        <Flex.Row>
          <Separator className={cx(isSeparatorVisible && '__visible')} />
          <Offer>
            <Flex.Row justifyContent="space-between" gap="s300">
              <LogoAndRoomWrapper>
                <ProviderLogo providerCode={offer.intermediaryProvider} />
                <RoomName variant="labelS">{roomName}</RoomName>
              </LogoAndRoomWrapper>
              <PriceAndCtaWrapper>
                <PriceAndNightMessage alignItems="flex-end">
                  <OfferPrice showRowLayout alwaysShowNightlyPrice offer={offer as SearchOffer} anchorPrice={undefined} anchorPriceTotal={undefined} size="md" hotelId={hotelId} />
                  <Text variant="bodyXS" colorPath="content.neutral.c600">
                    {formatMessage(messages.perNight)}
                  </Text>
                </PriceAndNightMessage>

                <OfferCtaWrapper>
                  <OfferTrackingContextProvider parameters={searchParameters} offerEntity={offerEntity} splitBookingDataProps={{
                  isSplitBooking: true,
                  splitBookingPosition,
                  offerPositionInsideBundle: offerIdx === 0 ? 'a' : 'b'
                }}>
                    <OfferInteraction key={offer.id} component={getEventComponentName()} hotelId={hotelId} offer={offer as SearchOffer}
                  // TODO: Search TS preexisting issue
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  offerPosition={`${splitBookingPosition + 1}${offerIdx === 0 ? 'a' : 'b'}`} isSplitBooking={true}>
                      <OfferCta hotelId={hotelId} offer={offer} splitBookingType={splitBookingType} />
                    </OfferInteraction>
                  </OfferTrackingContextProvider>
                </OfferCtaWrapper>
              </PriceAndCtaWrapper>
            </Flex.Row>
          </Offer>
        </Flex.Row>
      </OfferDetails>
    </Flex.Row>;
};

require("./SplitBookingDetailsOfferItem.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SplitBookingDetailsOfferItem.tsx");